import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';

export interface DocumentItem {
  id: number;
  ticketId?: number;
  ticketCommentId?: number;
  installationId?: number;
  subtaskKey?: string | null;
  scope: number;
  s3Key: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: number;
  createdAt: string;
}

interface DocumentListProps {
  ticketId?: number;
  ticketCommentId?: number;
  installationId?: number;
  refreshKey?: number;
  canDelete?: boolean;
  // When set, only documents tagged with this subtaskKey are listed (client-side
  // filter). Used by the Installation Checklist to show only its own uploads
  // (e.g. the PV module plan) rather than every installation document.
  subtaskKey?: string;
  // When set, the delete button is shown only for documents uploaded by this
  // user (partners may delete only their own uploads). Omit to allow all.
  deleteOwnerId?: number;
  // Checklist evidence (anything carrying a subtaskKey) is frozen by the on-site
  // checklist signature. Set this so the button is absent rather than erroring.
  lockedSubtaskDocs?: boolean;
  // Documents inlined in a comment/description body via ![](/DownloadDocument?id=N).
  // Deleting one would leave a permanently broken image in the text, so they get
  // no delete button — the way to remove them is to edit the text.
  protectedIds?: number[];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function isVideo(contentType: string): boolean {
  return contentType.startsWith('video/');
}

function getFileIcon(contentType: string) {
  if (contentType === 'application/pdf') return <PictureAsPdfIcon fontSize="small" color="error" />;
  if (isVideo(contentType)) return <VideocamIcon fontSize="small" color="primary" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

function DocumentList({
  ticketId,
  ticketCommentId,
  installationId,
  refreshKey = 0,
  canDelete = false,
  subtaskKey,
  deleteOwnerId,
  lockedSubtaskDocs = false,
  protectedIds
}: DocumentListProps) {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [videoUrls, setVideoUrls] = useState<Record<number, string>>({});
  const [loadingVideoIds, setLoadingVideoIds] = useState<Set<number>>(new Set());
  // Every blob URL this list has created, held outside React state so the unmount
  // cleanup always sees the complete set. A document is either an image or a
  // video, so one map keyed by document id covers `previews` and `videoUrls` both.
  const createdUrlsRef = useRef<Record<number, string>>({});

  // A refreshKey bump refetches while thumbnails are still downloading, and the
  // duplicate-download guard below reads lagging state — so the same document can
  // resolve twice. Drop the superseded URL instead of orphaning it: nothing points
  // at it any more, and only the entry still in the ref gets revoked on unmount.
  const rememberUrl = (docId: number, url: string) => {
    const superseded = createdUrlsRef.current[docId];
    if (superseded) window.URL.revokeObjectURL(superseded);
    createdUrlsRef.current[docId] = url;
  };
  const intl = useIntl();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [expandedVideo, setExpandedVideo] = useState<{ url: string; contentType: string } | null>(null);

  const loadVideoBlob = (doc: DocumentItem) => {
    if (videoUrls[doc.id] || loadingVideoIds.has(doc.id)) return;
    setLoadingVideoIds((prev) => {
      const next = new Set(prev);
      next.add(doc.id);
      return next;
    });
    axiosConfig
      .get('/DownloadDocument', {
        params: { id: doc.id },
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.contentType }));
        rememberUrl(doc.id, url);
        setVideoUrls((prev) => ({ ...prev, [doc.id]: url }));
      })
      .catch(() => {})
      .finally(() => {
        setLoadingVideoIds((prev) => {
          const next = new Set(prev);
          next.delete(doc.id);
          return next;
        });
      });
  };

  const fetchDocuments = () => {
    setLoading(true);
    // Drop any stale failure message: the list about to render is fresh.
    setDeleteError(null);
    axiosConfig
      .get('/GetDocuments', {
        params: { ticketId, ticketCommentId, installationId }
      })
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const docs = subtaskKey
          ? res.data.filter((d: DocumentItem) => d.subtaskKey === subtaskKey)
          : res.data;
        setDocuments(docs);
      })
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDocuments();
  }, [ticketId, ticketCommentId, installationId, refreshKey, subtaskKey]);

  // Load image thumbnails
  useEffect(() => {
    documents.forEach((doc) => {
      if (isImage(doc.contentType) && !previews[doc.id]) {
        axiosConfig
          .get('/DownloadDocument', {
            params: { id: doc.id },
            responseType: 'blob'
          })
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.contentType }));
            rememberUrl(doc.id, url);
            setPreviews((prev) => ({ ...prev, [doc.id]: url }));
          })
          .catch(() => {});
      }
    });
  }, [documents]);

  // Revoke every blob URL once, on unmount.
  //
  // Do NOT key this on `previews`/`videoUrls`: React runs the previous cleanup
  // before each re-run, so each newly loaded thumbnail would revoke all the ones
  // loaded before it. The damage is invisible at first — an <img> that already
  // decoded its blob keeps rendering after the URL dies — but reopening that
  // thumbnail in the dialog mounts a fresh <img> against a dead URL (broken
  // image), and handleDownload reuses the same dead URL (silent failure).
  //
  // Reading state here with empty deps would capture the initial {} and revoke
  // nothing, which is what keying on state was working around; the ref is stable,
  // so it sees every URL ever created and both problems go away.
  useEffect(() => {
    const createdUrls = createdUrlsRef.current;
    return () => {
      Object.values(createdUrls).forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, []);

  const handleDownload = (doc: DocumentItem) => {
    const cached = previews[doc.id] || videoUrls[doc.id];
    if (cached) {
      const link = document.createElement('a');
      link.href = cached;
      link.setAttribute('download', doc.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    axiosConfig
      .get('/DownloadDocument', {
        params: { id: doc.id },
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.originalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  // Deleting removes the S3 object, so confirm first — there is no undo.
  // A signed checklist answers 400; anything else is treated as a generic failure.
  // Without this the row would simply not disappear, with no explanation.
  const handleDelete = (doc: DocumentItem) => {
    const confirmText = intl.formatMessage(
      {
        id: 'confirmDeleteDocument',
        defaultMessage: 'Delete "{name}"? This cannot be undone.'
      },
      { name: doc.originalName }
    );
    if (!window.confirm(confirmText)) return;

    setDeleteError(null);
    axiosConfig
      .delete('/DeleteDocument', { params: { id: doc.id } })
      .then(() => fetchDocuments())
      .catch((err) => {
        setDeleteError(
          err?.response?.status === 400
            ? 'checklistSignedLocked'
            : 'documentDeleteFailed'
        );
      });
  };

  if (loading) return null;
  if (documents.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        <FormattedMessage id="attachments" defaultMessage="Attachments" />
        <Chip label={documents.length} size="small" sx={{ ml: 1 }} />
      </Typography>
      <List dense disablePadding>
        {documents.map((doc) => (
          <ListItem
            key={doc.id}
            disableGutters
            sx={{ alignItems: 'flex-start', flexDirection: 'column', gap: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {!isImage(doc.contentType) && (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  {getFileIcon(doc.contentType)}
                </Box>
              )}
              <ListItemText
                primary={doc.originalName}
                secondary={`${formatFileSize(doc.sizeBytes)} — ${new Date(doc.createdAt).toLocaleDateString()}`}
              />
              <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                <IconButton size="small" onClick={() => handleDownload(doc)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
                {canDelete &&
                  (deleteOwnerId == null ||
                    doc.uploadedByUserId === deleteOwnerId) &&
                  !(lockedSubtaskDocs && doc.subtaskKey) &&
                  !protectedIds?.includes(doc.id) && (
                    <IconButton size="small" onClick={() => handleDelete(doc)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
              </Box>
            </Box>
            {isImage(doc.contentType) && previews[doc.id] && (
              <Box
                component="img"
                src={previews[doc.id]}
                alt={doc.originalName}
                onClick={() => setExpandedImage(previews[doc.id])}
                sx={{
                  maxWidth: 200,
                  maxHeight: 150,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { opacity: 0.85 }
                }}
              />
            )}
            {isVideo(doc.contentType) && (
              videoUrls[doc.id] ? (
                <Box
                  component="video"
                  controls
                  src={videoUrls[doc.id]}
                  onClick={() => setExpandedVideo({ url: videoUrls[doc.id], contentType: doc.contentType })}
                  sx={{
                    maxWidth: 240,
                    maxHeight: 160,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    backgroundColor: 'common.black'
                  }}
                />
              ) : (
                <Box
                  onClick={() => loadVideoBlob(doc)}
                  sx={{
                    width: 240,
                    height: 135,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'action.hover',
                    cursor: loadingVideoIds.has(doc.id) ? 'progress' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 0.5,
                    '&:hover': { opacity: 0.85 }
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {loadingVideoIds.has(doc.id) ? (
                      <FormattedMessage id="videoLoading" defaultMessage="Loading…" />
                    ) : (
                      <FormattedMessage id="videoClickToPlay" defaultMessage="Click to play" />
                    )}
                  </Typography>
                </Box>
              )
            )}
          </ListItem>
        ))}
      </List>

      {deleteError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {deleteError === 'checklistSignedLocked' ? (
            <FormattedMessage
              id="checklistSignedLocked"
              defaultMessage="The checklist is signed — its files can no longer be removed."
            />
          ) : (
            <FormattedMessage
              id="documentDeleteFailed"
              defaultMessage="Could not delete this file."
            />
          )}
        </Typography>
      )}

      {/* Full-size image preview dialog */}
      <Dialog
        open={!!expandedImage}
        onClose={() => setExpandedImage(null)}
        maxWidth="lg"
      >
        {expandedImage && (
          <Box
            component="img"
            src={expandedImage}
            onClick={() => setExpandedImage(null)}
            sx={{ maxWidth: '90vw', maxHeight: '90vh', cursor: 'pointer' }}
          />
        )}
      </Dialog>

      {/* Full-size video preview dialog */}
      <Dialog
        open={!!expandedVideo}
        onClose={() => setExpandedVideo(null)}
        maxWidth="lg"
      >
        {expandedVideo && (
          <Box
            component="video"
            controls
            autoPlay
            src={expandedVideo.url}
            sx={{ maxWidth: '90vw', maxHeight: '90vh', backgroundColor: 'common.black' }}
          />
        )}
      </Dialog>
    </Box>
  );
}

export default DocumentList;
